/** Keyboard-only focus rings for scripted focus moves. */
(() => {
  if (window.DieuxFocusVisibility) return;

  const nativeFocus = HTMLElement.prototype.focus;
  const nativeBlur = HTMLElement.prototype.blur;
  if (typeof nativeFocus !== 'function' || typeof nativeBlur !== 'function') return;

  const NAVIGATION_KEYS = new Set(['Tab']);
  const CONTROL_NAVIGATION_KEYS = new Set([
    'Enter',
    ' ',
    'Spacebar',
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End',
    'PageUp',
    'PageDown',
  ]);

  const TEXT_ENTRY_SELECTOR = [
    'input:not([type="button"],[type="checkbox"],[type="color"],[type="file"],[type="hidden"],[type="image"],[type="radio"],[type="range"],[type="reset"],[type="submit"])',
    'textarea',
    '[contenteditable]:not([contenteditable="false"])',
  ].join(',');

  function isTextEntry(node) {
    return node instanceof Element && node.matches(TEXT_ENTRY_SELECTOR);
  }

  let hadKeyboardEvent = false;

  const root = document.documentElement;
  const publishModality = () => {
    const modality = hadKeyboardEvent ? 'keyboard' : 'pointer';
    if (root.dataset.focusModality !== modality) root.dataset.focusModality = modality;
  };
  publishModality();

  const modalityListeners = new Set();
  let modalityObserver;

  /**
   * Calls a listener with the published modality whenever `data-focus-modality` changes.
   *
   * @param {(modality: string | undefined) => void} listener - Change callback.
   * @returns {() => void} Removes the listener.
   */
  function subscribe(listener) {
    if (typeof listener !== 'function') return () => {};

    modalityListeners.add(listener);
    if (!modalityObserver && typeof MutationObserver === 'function') {
      modalityObserver = new MutationObserver(() => {
        const modality = root.dataset.focusModality;
        modalityListeners.forEach((callback) => {
          try {
            callback(modality);
          } catch (error) {
            window.reportError?.(error);
          }
        });
      });
      modalityObserver.observe(root, { attributes: true, attributeFilter: ['data-focus-modality'] });
    }
    return () => modalityListeners.delete(listener);
  }

  window.addEventListener(
    'keydown',
    (event) => {
      if (event.metaKey || event.ctrlKey || (event.altKey && event.key !== 'Tab')) return;
      if (typeof event.key !== 'string') return;

      const isNavigation =
        NAVIGATION_KEYS.has(event.key) ||
        (CONTROL_NAVIGATION_KEYS.has(event.key) && !isTextEntry(event.target));

      if (!isNavigation) return;
      hadKeyboardEvent = true;
      publishModality();
    },
    { capture: true, passive: true }
  );

  const onPointerInput = (event) => {
    if (!event.isTrusted) return;
    hadKeyboardEvent = false;
    publishModality();
  };

  ['pointerdown', 'mousedown', 'touchstart'].forEach((type) => {
    window.addEventListener(type, onPointerInput, { capture: true, passive: true });
  });

  let supportsFocusVisible = false;
  try {
    const probe = {};
    Object.defineProperty(probe, 'focusVisible', {
      get() {
        supportsFocusVisible = true;
        return true;
      },
    });
    nativeFocus.call(document.createElement('button'), probe);
  } catch {
    supportsFocusVisible = false;
  }

  window.DieuxFocusVisibility = { patched: supportsFocusVisible, subscribe };
  // Engines without the focusVisible focus option cannot have the ring suppressed from script;
  // CSS keyed on this attribute plus the modality is the only lever left there.
  root.dataset.focusVisiblePatched = String(supportsFocusVisible);
  if (!supportsFocusVisible) return;

  /** Drop the ring from an element that already holds focus. */
  function quietCurrentFocus(element) {
    if (!(element instanceof HTMLElement) || isTextEntry(element)) return;
    const root = element.ownerDocument;
    if (root.activeElement !== element || !element.matches(':focus-visible')) return;

    nativeBlur.call(element);
    nativeFocus.call(element, { preventScroll: true, focusVisible: false });
    if (root.activeElement !== element) nativeFocus.call(element, { preventScroll: true });
  }

  HTMLElement.prototype.focus = function focus(options) {
    const keepEngineDefault =
      hadKeyboardEvent ||
      isTextEntry(this) ||
      (options !== null && typeof options === 'object' && 'focusVisible' in options);

    if (keepEngineDefault) {
      nativeFocus.call(this, options);
      return;
    }

    if (this.ownerDocument.activeElement === this) {
      quietCurrentFocus(this);
      return;
    }

    nativeFocus.call(this, { ...options, focusVisible: false });
  };

  const nativeShowModal =
    typeof HTMLDialogElement === 'function' ? HTMLDialogElement.prototype.showModal : null;

  if (typeof nativeShowModal === 'function') {
    HTMLDialogElement.prototype.showModal = function showModal() {
      nativeShowModal.call(this);
      if (hadKeyboardEvent) return;

      const doc = this.ownerDocument;
      quietCurrentFocus(doc.activeElement);
      // Opening during load, the engine re-lands focus on the dialog itself one frame later.
      requestAnimationFrame(() => {
        if (!hadKeyboardEvent) quietCurrentFocus(doc.activeElement);
      });
    };
  }

  document.addEventListener(
    'close',
    (event) => {
      if (!(event.target instanceof HTMLDialogElement)) return;
      if (!hadKeyboardEvent) quietCurrentFocus(event.target.ownerDocument.activeElement);
    },
    { capture: true }
  );
})();
